import {useState, useEffect} from 'react'
import usePostApi from '../../custom_hooks/usePostApi'
import {useThemeMode} from '../../../../_metronic/partials'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import ThemeColors from '../ThemePref'
import Select, {components} from 'react-select'
import {KTIcon} from '../../../../_metronic/helpers'

type Data = {
  pod_business_name: string
  pod_brand_name: string
  pod_industry_id: number | string
  pod_team_size: number
  pod_description: string
  pod_country_code: string
  pod_website_url: string
  dateFormat: string
  timeZone: string
  pod_pref_color: string
  pod_currency_code: string
  data_center_id: string
}

interface Step1Props {
  set2ndIndex(): void
  data: Data
  setData: (values: Data) => void
}

const registrationSchema = Yup.object().shape({
  pod_business_name: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(250, 'Maximum 250 characters')
    .required('Organization Name is required'),
  pod_brand_name: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(250, 'Maximum 250 characters')
    .required('Brand Name is required'),
  pod_country_code: Yup.string().required('Country is required'),
  pod_currency_code: Yup.string().required('Currency is required'),
  timeZone: Yup.string(),
  dateFormat: Yup.string(),
  pod_pref_color: Yup.string(),
})

const Step1 = (props: Step1Props) => {
  const {mode} = useThemeMode()
  const {execute} = usePostApi()
  const {data, setData} = props
  const onSubmit = async (Values: Data) => {
    props.setData({...Values})
    props.set2ndIndex()
  }

  const updateData = (value: string, key: string) => {
    setData({...data, [key]: value})
  }

  const [countryOptions, setCountryOptions] = useState([])
  const [currencies, setCurrencies] = useState([])

  const getCountries = async () => {
    try {
      const url = '/master/country'
      const formData = {}

      const response = await execute(url, 'POST', formData)
      const options = (response.data || []).map((each: any) => ({
        pref_date_format: each.pref_date_format,
        pref_timezone: each.pref_timezone,
        value: each.iso,
        label: each.name,
      }))
      setCountryOptions(options)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getCurrencies = async () => {
    try {
      const url = `/master/currency`
      const formData = {}
      const response = await execute(url, 'POST', formData)
      const options = (response.data || []).map((each: any) => ({
        value: each.currency_code,
        label: each.currency_name,
      }))
      setCurrencies(options)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  useEffect(() => {
    getCountries()
    getCurrencies()
  }, [])

  return (
    <Formik validationSchema={registrationSchema} initialValues={props.data} onSubmit={onSubmit}>
      {({setFieldValue, values}: any) => {
        return (
          <Form className='w-100'>
            {/* <div className='w-100 d-flex flex-column justify-content-between mb-7 fv-row'> */}
            <div className='w-100 mb-5'>
              <label
                className='d-flex align-items-center fs-6 fw-semibold form-label mb-2'
                htmlFor='org_name'
              >
                <span className='required'>Organization Name</span>
                <i
                  className='fas fa-info-circle ms-2 fs-7'
                  data-bs-toggle='tooltip'
                  title='This is the name of your company or organization.'
                ></i>
              </label>
              <Field
                id='org_name'
                type='text'
                name='pod_business_name'
                className='form-control form-control-solid'
                placeholder='Enter your organization name'
                autoFocus
              />
              <div className='text-danger mt-2'>
                <ErrorMessage name='pod_business_name' />
              </div>
            </div>
            <div className='d-flex flex-column flex-lg-row align-items-start gap-5 mb-5'>
              <div className='w-100 w-lg-50'>
                <label
                  className='d-flex align-items-center fs-6 fw-semibold form-label mb-2'
                  htmlFor='brand_name'
                >
                  <span className='required'>Brand Name</span>
                  <i
                    className='fas fa-info-circle ms-2 fs-7'
                    data-bs-toggle='tooltip'
                    title='The name of your brand or product.'
                  ></i>
                </label>
                <Field
                  id='brand_name'
                  type='text'
                  name='pod_brand_name'
                  className='form-control form-control-solid'
                  placeholder='Enter your brand name'
                />
                <div className='text-danger mt-2'>
                  <ErrorMessage name='pod_brand_name' />
                </div>
              </div>

              <div className='w-100 w-lg-50'>
                <ThemeColors color={values.pod_pref_color} setFieldValue={setFieldValue} />
              </div>
            </div>

            <div className='d-flex flex-column flex-lg-row gap-5 mb-5'>
              <div className='w-100 w-mg-50'>
                <label className='d-flex align-items-center fs-6 fw-semibold form-label mb-2'>
                  <span className='required'>Country</span>
                  <i
                    className='fas fa-info-circle ms-2 fs-7'
                    data-bs-toggle='tooltip'
                    title='Choose the country where your organization is located.'
                  ></i>
                </label>

                <Select
                  value={countryOptions.find(
                    (option: any) => option.value === values.pod_country_code
                  )}
                  styles={{
                    control: (baseStyles: any, state: any) => ({
                      ...baseStyles,
                      backgroundColor: mode === 'dark' ? '#1b1b29' : '#f9f9f9',
                      color: '#ffffff',
                      borderWidth: '0px',
                    }),
                    menu: (baseStyles: any) => ({
                      ...baseStyles,
                      backgroundColor: mode === 'dark' ? '#2b2b40' : '#f9f9f9',
                    }),
                    singleValue: (baseStyles: any, state: any) => ({
                      ...baseStyles,
                      color: mode === 'dark' ? '#92929f' : '#000000',
                    }),
                    input: (baseStyles: any) => ({
                      ...baseStyles,
                      color: mode === 'dark' ? '#92929f' : '#000000', // Color for the search text
                    }),
                    option: (baseStyles: any, {isFocused, isSelected}: any) => ({
                      ...baseStyles,
                      backgroundColor: isSelected
                        ? '#C2C2C2'
                        : isFocused
                        ? '#e0e0e0'
                        : baseStyles.backgroundColor,
                      color: isSelected ? '#000000' : isFocused ? '#000000' : baseStyles.color,
                    }),
                  }}
                  theme={(theme: any) => ({
                    ...theme,
                    borderRadius: 5,
                    backgroundColor: '#000000',
                    colors: {
                      ...theme.colors,
                      primary25: 'rgba(80, 107, 236, 0.3)',
                      primary: mode === 'dark' ? '#1b1b29' : '#f9f9f9',
                    },
                  })}
                  className='react-select-container'
                  classNamePrefix='react-select'
                  options={countryOptions}
                  isSearchable
                  placeholder='Select your country'
                  // onChange={(val: any) => handleCountryChange(setFieldValue, val)}
                  onChange={(val: any) => {
                    setFieldValue('pod_country_code', val.value || '')
                    setFieldValue('timeZone', val.pref_timezone || '')
                    setFieldValue('dateFormat', val.pref_date_format || '')
                  }}
                />
                <div className='text-danger'>
                  <ErrorMessage name='pod_country_code' />
                </div>
              </div>
              <div className='w-100 w-mg-50'>
                <label className='d-flex align-items-center fs-6 fw-semibold form-label mb-2'>
                  <span className='required'>Currency</span>
                  <i
                    className='fas fa-info-circle ms-2 fs-7'
                    data-bs-toggle='tooltip'
                    title='Select the currency your organization uses for transactions.'
                  ></i>
                </label>
                <Select
                  value={currencies.find(
                    (option: any) => option.value === values.pod_currency_code
                  )}
                  styles={{
                    control: (baseStyles: any, state: any) => ({
                      ...baseStyles,
                      backgroundColor: mode === 'dark' ? '#1b1b29' : '#f9f9f9',
                      color: '#ffffff',
                      borderWidth: '0px',
                    }),
                    menu: (baseStyles: any) => ({
                      ...baseStyles,
                      backgroundColor: mode === 'dark' ? '#2b2b40' : '#f9f9f9',
                      // maxHeight: '160px',
                      // overflowY: 'scroll',
                    }),
                    singleValue: (baseStyles: any, state: any) => ({
                      ...baseStyles,
                      color: mode === 'dark' ? '#92929f' : '#000000',
                    }),
                    input: (baseStyles: any) => ({
                      ...baseStyles,
                      color: mode === 'dark' ? '#92929f' : '#000000', // Color for the search text
                    }),
                    option: (baseStyles: any, {isFocused, isSelected}: any) => ({
                      ...baseStyles,
                      backgroundColor: isSelected
                        ? '#C2C2C2'
                        : isFocused
                        ? '#e0e0e0'
                        : baseStyles.backgroundColor,
                      color: isSelected ? '#000000' : isFocused ? '#000000' : baseStyles.color,
                    }),
                  }}
                  theme={(theme: any) => ({
                    ...theme,
                    borderRadius: 5,
                    backgroundColor: '#000000',
                    colors: {
                      ...theme.colors,
                      primary25: 'rgba(80, 107, 236, 0.5)',
                      primary: mode === 'dark' ? '#1b1b29' : '#f9f9f9',
                    },
                  })}
                  className='react-select-container'
                  classNamePrefix='react-select'
                  options={currencies}
                  isSearchable
                  placeholder='Select your currency'
                  onChange={(val: any) => {
                    setFieldValue('pod_currency_code', val?.value || '')

                    // const isAlreadySelected = data.pod_currency_code === val?.value

                    // if (!isAlreadySelected) {
                    //   updateData(val?.value, 'pod_currency_code')
                    // }
                  }}
                  // components={{Option: MultiLineOption}} // Add this line to use the MultiLineOption component
                />
                <div className='text-danger mt-2'>
                  <ErrorMessage name='pod_currency_code' />
                </div>
              </div>
            </div>
            <div className='alert alert-danger d-flex align-items-center p-1'>
              <KTIcon iconName='information' className='fs-2 text-danger me-2' />
              <div className='d-flex flex-column'>
                <h6 className='mb-0 fw-semibold fs-7 text-dark'>Note:</h6>
                <span className='fs-8 text-dark'>
                  Country selection is final. Currency is for reference only; payments are processed
                  in USD.
                </span>
              </div>
            </div>

            <button
              type='submit'
              className='d-none btn btn-lg btn-primary w-100'
              id='verify-sign-up-btn'
            >
              Submit
            </button>
          </Form>
        )
      }}
    </Formik>
  )
}

export {Step1}
